import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { NavBarItem } from '@core/config';

import { SidebarId } from '../../layout.constants';
import { MwSidebarService } from '../sidebar/sidebar.service';
import { MwSidebarComponent } from './../sidebar/sidebar.component';
import { NavListDataService } from './nav-list-data.service';

export type MwListState = 'open' | 'closed';

@Component({
  selector: 'mw-nav-list',
  templateUrl: './nav-list.component.html',
  styleUrls: ['./nav-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [NavListDataService],
})
export class MwNavListComponent implements OnInit, OnDestroy {
  currentRoute: string;
  state: MwListState = 'closed';
  items: Readonly<NavBarItem[]> = [];

  @ViewChild('navList') navList: ElementRef;

  @Input() sidebarFolded = false;
  @Output() opened = new EventEmitter<void>();
  @Output() scrollToTop = new EventEmitter<number>();

  private _navSidebar: MwSidebarComponent;
  private _closeMenuOnLeave = false;
  private _unsubscribeAll$ = new Subject<void>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly sidebarService: MwSidebarService,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    public readonly dataService: NavListDataService
  ) {
    this.subscribeToNavBarListHeightChange();
  }

  ngOnInit(): void {
    this.subscribeToNavSidebar();
    this.subscribeToNavBarItems();
  }

  @HostListener('mouseover', [`$event`])
  onMouseOver(event: any): void {
    if (this.sidebarFolded) {
      this._closeMenuOnLeave = true;
      this.unfoldNavSidebar();
    }

    event.preventDefault();
    event.stopPropagation();
  }

  @HostListener('mouseleave', [`$event`])
  onMouseLeave(event: Event): void {
    if (this._closeMenuOnLeave) {
      this._closeMenuOnLeave = false;
      this.foldNavSidebar();
      this.scrollToTop.emit();
    }

    event.preventDefault();
    event.stopPropagation();
  }

  ngOnDestroy(): void {
    this._unsubscribeAll$.next();
    this._unsubscribeAll$.complete();
  }

  onItemClick(event: Event, item: NavBarItem): void {
    if (!item) {
      return;
    }

    event.stopPropagation();

    if (item.route) {
      if (this.router.url?.includes('unauthorized')) {
        this.router.navigate(['/', 'pages', item.route]);
      } else {
        this.router.navigate([item.route], { relativeTo: this.route });
      }
      return;
    }
    if(item.url){
        window.open(item.url, '_blank');
        return;
    }
    this.dataService.setActiveItem(item);
    this.dataService.toggleItem(item);

    if (item.isOpened) {
      this.opened.emit();
    }
  }

  private foldNavSidebar(): void {
    if (!this._navSidebar) {
      return;
    }

    this._navSidebar.fold();
  }

  private unfoldNavSidebar(): void {
    if (!this._navSidebar) {
      return;
    }

    this._navSidebar.unfold();
  }

  private subscribeToNavSidebar(): void {
    this.sidebarService
      .getSidebar(SidebarId.Navigation)
      .pipe(takeUntil(this._unsubscribeAll$))
      .subscribe((sidebar) => (this._navSidebar = sidebar));
  }

  private subscribeToNavBarItems(): void {
    this.dataService.items$
      .pipe(takeUntil(this._unsubscribeAll$))
      .subscribe((items) => {
        this.items = items;
        this._changeDetectorRef.detectChanges();
      });
  }

  private subscribeToNavBarListHeightChange(): void {
    this.dataService.heightReduced
      .pipe(takeUntil(this._unsubscribeAll$))
      .subscribe((size) => {
        if (this.navList) {
          const navListEl = this.navList.nativeElement as HTMLElement;
          if (!this.dataService.activatedItem?.parent) {
            this.scrollToTop.emit(navListEl.scrollTop - size);
          }
        }
      });
  }
}
